import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';

interface SaveLeaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  title?: string;
  defaultName?: string;
  loading?: boolean;
}

const SaveLeaseDialog: React.FC<SaveLeaseDialogProps> = ({
  open,
  onClose,
  onSave,
  title = "Save Lease Agreement",
  defaultName = '',
  loading = false
}) => {
  const [leaseName, setLeaseName] = useState(defaultName);
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!leaseName.trim()) {
      setError('Please enter a name for the lease');
      return;
    }
    onSave(leaseName.trim());
  };

  const handleClose = () => {
    setLeaseName(defaultName);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give your lease a name to easily identify it later
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Lease Name"
            value={leaseName}
            onChange={(e) => {
              setLeaseName(e.target.value);
              setError('');
            }}
            placeholder="e.g., 123 Main St - John Doe"
            error={!!error}
            helperText={error}
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleSave();
              }
            }}
          />
          {defaultName && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current name: {defaultName}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          sx={{ borderRadius: 2, minWidth: 100 }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveLeaseDialog; 